import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_RECURRING_ORDER_ROUTER } from "../../../services/api-config";

// ===== Enums =====
export enum FREQUENCY_TYPE {
  WEEKLY = 1,
  INTERVAL_DAYS = 2,
  MONTHLY = 3,
}

export const FREQUENCY_TYPE_NAME: Record<number, string> = {
  [FREQUENCY_TYPE.WEEKLY]: "Hàng tuần",
  [FREQUENCY_TYPE.INTERVAL_DAYS]: "Theo ngày",
  [FREQUENCY_TYPE.MONTHLY]: "Hàng tháng",
};

export enum RECURRING_STATUS {
  PAUSED = 0,
  ACTIVE = 1,
  ENDED = 2,
}

export const RECURRING_STATUS_NAME: Record<number, string> = {
  [RECURRING_STATUS.PAUSED]: "Tạm dừng",
  [RECURRING_STATUS.ACTIVE]: "Đang hoạt động",
  [RECURRING_STATUS.ENDED]: "Đã kết thúc",
};

export const RECURRING_STATUS_COLOR: Record<number, string> = {
  [RECURRING_STATUS.PAUSED]: "yellow",
  [RECURRING_STATUS.ACTIVE]: "green",
  [RECURRING_STATUS.ENDED]: "gray",
};

export const DAY_OF_WEEK_NAME: Record<number, string> = {
  0: "CN",
  1: "T2",
  2: "T3",
  3: "T4",
  4: "T5",
  5: "T6",
  6: "T7",
};

// ===== Interfaces =====
export interface RecurringTemplateItem {
  item_id: number;
  item_type: number;
  quantity: number;
  price?: number;
}

export interface RecurringOrder {
  id: number;
  customer_id: number;
  branch_id: number;
  frequency_type: number;
  frequency_value: string;
  start_date: string;
  end_date: string | null;
  next_order_date: string;
  preferred_time: string;
  template_items: RecurringTemplateItem[];
  is_pickup_laundry: number;
  requires_counting?: number;
  prefill_count_from_last?: number;
  // Nhân sự mặc định gán CỨNG cho mọi đơn cron sinh ra sau này (null/rỗng = chưa gán, sẽ broadcast như cũ).
  pickup_driver_ids?: number[] | null;
  counter_ids?: number[] | null;
  /** Tài xế giao mặc định (xe máy) — bỏ qua khi có xe tải giao mặc định */
  delivery_driver_ids?: number[] | null;
  /** NV kiểm đếm khi giao mặc định — chỉ đơn kiểm đếm */
  delivery_counter_ids?: number[] | null;
  /** Xe tải đi thẳng mặc định theo chiều (null = xe máy) */
  pickup_vehicle_id?: number | null;
  delivery_vehicle_id?: number | null;
  note: string;
  address: string;
  payment_method: number;
  is_debit: number;
  amount_discount: number;
  workflow_type: number;
  status: number;
  total_orders_created: number;
  last_order_id: number | null;
  last_order_date: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface RecurringOrderListFilters {
  branch_id?: number;
  customer_id?: number;
  status?: number;
  frequency_type?: number;
}

export interface CreateRecurringOrderPayload {
  customer_id: number;
  branch_id: number;
  frequency_type: number;
  frequency_value: string;
  start_date: string;
  end_date?: string;
  preferred_time?: string;
  template_items: RecurringTemplateItem[];
  is_pickup_laundry?: number;
  note?: string;
  address?: string;
  payment_method?: number;
  is_debit?: number;
  amount_discount?: number;
  workflow_type?: number;
}

export interface UpdateRecurringOrderPayload {
  frequency_type?: number;
  frequency_value?: string;
  end_date?: string;
  preferred_time?: string;
  template_items?: RecurringTemplateItem[];
  is_pickup_laundry?: number;
  note?: string;
  address?: string;
  payment_method?: number;
  is_debit?: number;
  amount_discount?: number;
  workflow_type?: number;
}

export interface AssignRecurringStaffPayload {
  pickup_driver_ids?: number[];
  counter_ids?: number[];
  delivery_driver_ids?: number[];
  delivery_counter_ids?: number[];
  /** null = bỏ xe */
  pickup_vehicle_id?: number | null;
  delivery_vehicle_id?: number | null;
}

export interface CreateFromOrderPayload {
  frequency_type: number;
  frequency_value: string;
  start_date: string;
  end_date?: string;
  preferred_time?: string;
}

// ===== Helpers =====
export function formatFrequency(type: number, value: string): string {
  switch (type) {
    case FREQUENCY_TYPE.WEEKLY: {
      const days = value.split(",").map((d) => DAY_OF_WEEK_NAME[parseInt(d.trim())] || d);
      return `Hàng tuần: ${days.join(", ")}`;
    }
    case FREQUENCY_TYPE.INTERVAL_DAYS:
      return `Mỗi ${value} ngày`;
    case FREQUENCY_TYPE.MONTHLY: {
      const dates = value.split(",").map((d) => `ngày ${d.trim()}`);
      return `Hàng tháng: ${dates.join(", ")}`;
    }
    default:
      return value;
  }
}

// ===== Service =====
const RecurringOrderService = {
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<RecurringOrder[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: RecurringOrderListFilters) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_LIST,
          data: params,
        }),
      response: baseResponse,
      loading,
    };
  },

  detail: () => {
    const { request, baseResponse, loading } = useFetchData.Get<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_DETAIL(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (payload: CreateRecurringOrderPayload) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  createFromOrder: () => {
    const { request, baseResponse, loading } = useFetchData.Post<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (orderId: number, payload: CreateFromOrderPayload) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_FROM_ORDER(orderId),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: UpdateRecurringOrderPayload) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_UPDATE(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  assignStaff: () => {
    const { request, baseResponse, loading } = useFetchData.Post<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: AssignRecurringStaffPayload) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_ASSIGN_STAFF(id),
          data: payload,
        }),
      response: baseResponse,
      loading,
    };
  },

  toggleStatus: () => {
    const { request, baseResponse, loading } = useFetchData.Post<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_TOGGLE(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },

  end: () => {
    const { request, baseResponse, loading } = useFetchData.Post<RecurringOrder>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_RECURRING_ORDER_ROUTER.API_END(id),
          data: {},
        }),
      response: baseResponse,
      loading,
    };
  },
};

export default RecurringOrderService;
